import type { InputProps } from "@aerstudios/aerated-aerinputstructure";
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { useAddressManagerContext } from ".";
import { Button } from "../../../components/common";
import { Cross } from "../../../components/common/Icons";
import LoadingIndicator from "../../../components/common/LoadingIndicator/LoadingIndicator";
import { Colours } from "../../../utils/colours";
import TextInput, {
	PlainTextInput,
	type TextInputProps,
} from "../TextInput/TextInput";
import * as styles from "./AddressManager.module.scss";

const AddressLookupInput: React.FC<InputProps> = () => {
	const [searchValue, setSearchValue] = useState<string | null>(null);

	const { queryAddresses } = useAddressManagerContext();

	return (
		<div className={styles.addresslookup}>
			<PlainTextInput
				id="address-lookup"
				label={"To find your address, please enter your postcode"}
				handleChange={setSearchValue}
				onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
					if (e.key === "Enter") {
						e.preventDefault();
						queryAddresses(searchValue);
					}
				}}
				className={styles.addresslookupfield}
			/>
			<div className={styles.addresslookupbuttonwrapper}>
				<Button
					onClick={() => queryAddresses(searchValue)}
					fullWidth
					variant="secondary"
					disabled={searchValue === null || searchValue?.length < 2}
				>
					Find Address
				</Button>
			</div>
		</div>
	);
};

const AddressLine1Input: React.FC<TextInputProps> = ({ ...textInputProps }) => {
	const [confirmedValue, setConfirmedValue] = useState(undefined);
	const { confirmedAddress } = useAddressManagerContext();
	useEffect(() => {
		if (confirmedAddress !== null) {
			setConfirmedValue(confirmedAddress.AddressLine1);
		}
	}, [confirmedAddress]);

	// This is to ensure that the value can be prefilled if we have it stored in cookie.
	// setting the value prop on the TextInput component will cause it to be uncontrolled
	const additionalProps: { value?: string } = {};
	if (confirmedValue !== undefined) {
		additionalProps.value = confirmedValue;
	}
	return <TextInput {...textInputProps} {...additionalProps} />;
};
const AddressLine2Input: React.FC<TextInputProps> = ({ ...textInputProps }) => {
	const [confirmedValue, setConfirmedValue] = useState(undefined);
	const { confirmedAddress } = useAddressManagerContext();
	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (confirmedAddress !== null) {
			timeout = setTimeout(
				() => setConfirmedValue(confirmedAddress.AddressLine2),
				50,
			);
		}
		return () => timeout && clearTimeout(timeout);
	}, [confirmedAddress]);

	// This is to ensure that the value can be prefilled if we have it stored in cookie.
	// setting the value prop on the TextInput component will cause it to be uncontrolled
	const additionalProps: { value?: string } = {};
	if (confirmedValue === "" || confirmedValue !== undefined) {
		additionalProps.value = confirmedValue;
	}

	return <TextInput {...textInputProps} {...additionalProps} />;
};
const TownCityInput: React.FC<TextInputProps> = ({ ...textInputProps }) => {
	const [confirmedValue, setConfirmedValue] = useState(undefined);
	const { confirmedAddress } = useAddressManagerContext();
	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (confirmedAddress !== null) {
			timeout = setTimeout(
				() => setConfirmedValue(confirmedAddress.TownCity),
				150,
			);
		}
		return () => timeout && clearTimeout(timeout);
	}, [confirmedAddress]);

	// This is to ensure that the value can be prefilled if we have it stored in cookie.
	// setting the value prop on the TextInput component will cause it to be uncontrolled
	const additionalProps: { value?: string } = {};
	if (confirmedValue !== undefined) {
		additionalProps.value = confirmedValue;
	}
	return <TextInput {...textInputProps} {...additionalProps} />;
};
const CountyInput: React.FC<TextInputProps> = ({ ...textInputProps }) => {
	const [confirmedValue, setConfirmedValue] = useState(undefined);
	const { confirmedAddress } = useAddressManagerContext();
	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (confirmedAddress !== null) {
			timeout = setTimeout(
				() => setConfirmedValue(confirmedAddress.County),
				200,
			);
		}
		return () => timeout && clearTimeout(timeout);
	}, [confirmedAddress]);

	// This is to ensure that the value can be prefilled if we have it stored in cookie.
	// setting the value prop on the TextInput component will cause it to be uncontrolled
	const additionalProps: { value?: string } = {};
	if (confirmedValue !== undefined) {
		additionalProps.value = confirmedValue;
	}
	return <TextInput {...textInputProps} {...additionalProps} />;
};

const CountryInput: React.FC<TextInputProps> = ({ ...textInputProps }) => {
	const [confirmedValue, setConfirmedValue] = useState(undefined);
	const { confirmedAddress } = useAddressManagerContext();
	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (confirmedAddress !== null) {
			timeout = setTimeout(
				() => setConfirmedValue(confirmedAddress.Country),
				250,
			);
		}
		return () => timeout && clearTimeout(timeout);
	}, [confirmedAddress]);

	// This is to ensure that the value can be prefilled if we have it stored in cookie.
	// setting the value prop on the TextInput component will cause it to be uncontrolled
	const additionalProps: { value?: string } = {};
	if (confirmedValue !== undefined) {
		additionalProps.value = confirmedValue;
	}
	return <TextInput {...textInputProps} {...additionalProps} />;
};
const PostcodeInput: React.FC<TextInputProps> = ({ ...textInputProps }) => {
	const [confirmedValue, setConfirmedValue] = useState(undefined);
	const { confirmedAddress } = useAddressManagerContext();
	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (confirmedAddress !== null) {
			timeout = setTimeout(
				() => setConfirmedValue(confirmedAddress.Postcode),
				100,
			);
		}
		return () => timeout && clearTimeout(timeout);
	}, [confirmedAddress]);

	// This is to ensure that the value can be prefilled if we have it stored in cookie.
	// setting the value prop on the TextInput component will cause it to be uncontrolled
	const additionalProps: { value?: string } = {};
	if (confirmedValue !== undefined) {
		additionalProps.value = confirmedValue;
	}
	return <TextInput {...textInputProps} {...additionalProps} />;
};

export const AddressPicker: React.FC = () => {
	const {
		addressResults,
		showAddressPicker,
		hideAddressPicker,
		querySingleAddress,
	} = useAddressManagerContext();

	useEffect(() => {
		if (showAddressPicker && addressResults[0]) {
			document.getElementById(addressResults[0].Text)?.focus(); // Focus trapping inside the modal when it loads.
		}
	}, [showAddressPicker, addressResults]);
	return (
		<ReactModal
			isOpen={showAddressPicker}
			preventScroll
			onRequestClose={hideAddressPicker}
			shouldCloseOnEsc
			shouldCloseOnOverlayClick
			shouldFocusAfterRender
			shouldReturnFocusAfterClose
			contentElement={() => (
				<div className={styles.addresspickermodal}>
					<button
						type="button"
						id={"close-address-picker"}
						className={styles.closebutton}
						onClick={hideAddressPicker}
						aria-label="Close popup"
					>
						<Cross fill={Colours.MIDNIGHT} height={"16px"} />
					</button>
					{addressResults.length === 0 ? (
						<div className={styles.loadingindicator}>
							<LoadingIndicator loading={true} scale={2.0} />
						</div>
					) : (
						<>
							<h4>Please confirm your address</h4>
							<div className={styles.addresspickerlist}>
								{addressResults.map((aR, i) => (
									<button
										type="button"
										id={aR.Text}
										tabIndex={0}
										key={aR.Id}
										className={styles.addresspickeritem}
										onClick={() => querySingleAddress(aR.Id)}
										onKeyDown={(e) => {
											// Focus trapping inside the modal - go back to the close button if you tab past the last address
											if (i === addressResults.length - 1 && e.key === "Tab") {
												e.preventDefault();
												document
													.getElementById("close-address-picker")
													?.focus();
											}
										}}
									>
										{aR.Text}
									</button>
								))}
							</div>
						</>
					)}
				</div>
			)}
			style={{
				overlay: {
					position: "fixed",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					paddingTop: "100px",
					height: "100%",
					width: "100%",
					padding: 0,
					margin: 0,
					zIndex: 999,
					boxSizing: "border-box",
					backgroundColor: "rgba(0, 0, 0, 0.5)",
				},
				content: {
					position: "relative",
					height: "100%",
					width: "100%",
					backgroundColor: "transparent",
					border: 0,
					margin: 0,
					marginTop: "100px",
					padding: 0,
					paddingTop: "25%",
					display: "flex",
					justifyContent: "center",
				},
			}}
		/>
	);
};
export default {
	AddressLookupInput,
	AddressLine1Input,
	AddressLine2Input,
	TownCityInput,
	CountyInput,
	CountryInput,
	PostcodeInput,
};
